/**
 * @module components/Help/HelpModal
 * @description Modal de ayuda contextual del sistema ACSGC.
 *
 * Tres pestañas:
 * - **FAQ**: Preguntas frecuentes sobre flujos del sistema
 * - **Atajos**: Atajos de teclado disponibles
 * - **Guías**: Accesos rápidos a documentación y videos
 *
 * @see Header que abre este modal desde el ícono de ayuda (?)
 */
import React, { useState } from 'react';
import { X, BookOpen, Video, MessageCircle, Keyboard, ExternalLink } from 'lucide-react';

interface HelpModalProps {
  onClose: () => void;
}

export function HelpModal({ onClose }: HelpModalProps) {
  const [activeTab, setActiveTab] = useState<'faq' | 'shortcuts' | 'guides'>('faq');

  const faqs = [
    {
      question: '¿Cómo asigno un control a un referente técnico?',
      answer: 'Selecciona un control en el sidebar, luego haz clic en el botón "Asignar Referente" en el panel de detalles. Selecciona uno o más referentes de la lista y confirma.'
    },
    {
      question: '¿Cómo marco un control como completado?',
      answer: 'Abre el control, completa todas las tareas requeridas y evidencias, luego cambia el estado a "Completado" en la sección de estado del control.'
    },
    {
      question: '¿Qué significan los diferentes estados de los controles?',
      answer: 'No Iniciado: Sin actividad. En Progreso: Trabajando activamente. En Revisión: Esperando validación. Completado: Implementado y verificado. No Conforme: No cumple requisitos.'
    },
    {
      question: '¿Cómo filtro controles por categoría o estado?',
      answer: 'En el sidebar, cambia a la pestaña "Filtros" para seleccionar filtros por estado, prioridad, asignados, categoría y rango de fechas.'
    },
    {
      question: '¿Cómo subo evidencias de cumplimiento?',
      answer: 'Abre el control, ve a la sección "Evidencias", haz clic en "Subir Evidencia", selecciona el archivo y agrega una descripción.'
    }
  ];

  const shortcuts = [
    { keys: ['Ctrl', 'K'], description: 'Buscar controles, tareas o evidencias' },
    { keys: ['Ctrl', 'B'], description: 'Toggle sidebar (expandir/colapsar)' },
    { keys: ['Esc'], description: 'Cerrar modales o paneles abiertos' },
    { keys: ['/', '?'], description: 'Abrir este menú de ayuda' }
  ];

  const guides = [
    {
      icon: <BookOpen className="w-5 h-5" />,
      title: 'Documentación Completa',
      description: 'Guía completa del sistema ISO 27001/27002',
      link: '#'
    },
    {
      icon: <Video className="w-5 h-5" />,
      title: 'Video Tutoriales',
      description: 'Aprende a usar el sistema con videos paso a paso',
      link: '#'
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: 'Contactar Soporte',
      description: 'Obtén ayuda de nuestro equipo de soporte',
      link: 'mailto:soporte@example.com'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Centro de Ayuda</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Encuentra respuestas y aprende a usar el sistema
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'faq'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Preguntas Frecuentes
          </button>
          <button
            onClick={() => setActiveTab('shortcuts')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'shortcuts'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Keyboard className="w-4 h-4 inline-block mr-1" />
            Atajos de Teclado
          </button>
          <button
            onClick={() => setActiveTab('guides')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'guides'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Guías y Recursos
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'faq' && (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'shortcuts' && (
            <div className="space-y-3">
              {shortcuts.map((shortcut, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {shortcut.description}
                  </span>
                  <div className="flex gap-1">
                    {shortcut.keys.map((key, keyIndex) => (
                      <kbd
                        key={keyIndex}
                        className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded shadow-sm"
                      >
                        {key}
                      </kbd>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 italic">
                * Usa Cmd en lugar de Ctrl en macOS
              </p>
            </div>
          )}

          {activeTab === 'guides' && (
            <div className="grid gap-4">
              {guides.map((guide, index) => (
                <a
                  key={index}
                  href={guide.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                >
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                    {guide.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      {guide.title}
                      <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {guide.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            ¿No encuentras lo que buscas?{' '}
            <a href="mailto:soporte@example.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              Contacta con soporte
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
